import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SolutionTimeline } from "@/components/SolutionTimeline";
import { ValueDelivered } from "@/components/ValueDelivered";
import { ClientLogosWithProfiles } from "@/components/ClientLogosWithProfiles";
import { StickyContactBar } from "@/components/StickyContactBar";
import { AtAGlanceBox } from "@/components/AtAGlanceBox";
import { LeadCaptureForm } from "@/components/LeadCaptureForm";
import OdooBooking from "@/components/OdooBooking";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { getProblem, getSolutionByProblem, getVertical, getClientsByVertical, getDashboardSettings } from "@/lib/api";
import { getIcon } from "@/lib/icons";
import { ArrowLeft, AlertTriangle, ArrowRight, Info, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function ProblemDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [problem, setProblem] = useState(null);
  const [solution, setSolution] = useState(null);
  const [vertical, setVertical] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [seoOverride, setSeoOverride] = useState(null);
  
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const problemRes = await getProblem(id);
        setProblem(problemRes.data);
        
        const [solutionRes, verticalRes] = await Promise.all([
          getSolutionByProblem(id),
          getVertical(problemRes.data.vertical_id)
        ]);
        
        setSolution(solutionRes.data);
        setVertical(verticalRes.data);
        
        // Fetch clients with full profiles
        try {
          const clientsRes = await getClientsByVertical(problemRes.data.vertical_id);
          setClients(clientsRes.data || []);
        } catch {
          // Fallback to old logos API if clients not available
          setClients([]);
        }
        // Load SEO override (admin AI-generated meta) — silently ignore if missing
        try {
          const seoRes = await import("@/lib/api").then(m => m.default.get(`/seo/entity-meta/problem/${id}`));
          if (seoRes?.data?.title || seoRes?.data?.description) setSeoOverride(seoRes.data);
        } catch (_) { /* no override */ }
      } catch (error) {
        toast.error("Failed to load problem details");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Handle sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerTop = headerRef.current.getBoundingClientRect().top;
        setIsHeaderSticky(headerTop <= 80);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-20 md:pt-24">
          <div className="max-w-5xl mx-auto px-4 py-16">
            <div className="animate-pulse space-y-8">
              <div className="h-8 w-32 bg-slate-200 rounded" />
              <div className="h-48 bg-slate-200 rounded-2xl" />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-24 bg-slate-200 rounded-xl" />
                  ))}
                </div>
                <div className="h-64 bg-slate-200 rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Problem Not Found</h2>
            <p className="text-slate-600 mb-6">The problem statement you&apos;re looking for doesn&apos;t exist.</p>
            <Link to="/verticals">
              <Button className="bg-[#0066CC] hover:bg-[#0052A3] rounded-full">
                Browse Industries
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Check if vertical has trust-building content
  const hasVerticalContent = vertical && (
    vertical.before_after?.length > 0 ||
    vertical.deliverables?.length > 0 ||
    vertical.testimonials?.length > 0 ||
    vertical.faq?.length > 0 ||
    vertical.roi_calculator?.enabled ||
    vertical.brochures?.length > 0 ||
    vertical.videos?.length > 0 ||
    vertical.competitor_comparison?.enabled ||
    vertical.implementation_timeline?.enabled
  );

  return (
    <div className="min-h-screen bg-white" data-testid="problem-detail-page">
      <SEOHead
        title={seoOverride?.title || (problem ? `${problem.title} - ${vertical?.name || 'Solutions'}` : "Solution Details")}
        description={seoOverride?.description || (problem ? `${problem.challenge?.slice(0, 155) || problem.description?.slice(0, 155)}` : "Explore detailed ERP solutions")}
        keywords={seoOverride?.focus_keyword || (problem ? `${problem.title}, ${vertical?.name || ''} ERP solution, ${problem.category || ''} automation, business process improvement` : "")}
        canonicalUrl={problem ? `/problem/${id}` : ""}
        ogType="article"
        breadcrumbs={problem ? [
          { name: "Home", url: "/" },
          { name: vertical?.name || "Industry", url: vertical ? `/vertical/${vertical.id}` : "/verticals" },
          { name: problem.title }
        ] : null}
        faqItems={vertical?.faq?.map(f => ({ question: f.question || f.q, answer: f.answer || f.a })) || null}
        howToSteps={solution?.steps && solution.steps.length > 0
          ? solution.steps.map(s => ({
              name: s.title || `Step ${s.step_number || ""}`,
              text: (s.description || "") + (s.duration ? ` (${s.duration})` : ""),
            }))
          : null}
        howToName={problem ? `Solving ${problem.title}` : ""}
        howToDescription={solution?.overview ? solution.overview.slice(0, 200) : ""}
      />
      <Navbar />
      
      <div className="pt-20 md:pt-24">
        {/* Breadcrumb & Initial Header */}
        <section className="py-6 md:py-8 bg-slate-50 border-b border-slate-100" ref={headerRef}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to={`/vertical/${vertical?.id}`}
              className="inline-flex items-center text-[#0066CC] hover:text-[#0052A3] text-sm mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to {vertical?.name || "Industry"}
            </Link>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 flex-shrink-0">
                {getIcon(problem.icon || "AlertCircle", "h-6 w-6")}
              </div>
              <div className="flex-1 min-w-0">
                {vertical && (
                  <span className="inline-block px-3 py-1 rounded-full bg-[#E6F0FA] text-[#0066CC] text-xs font-medium mb-2">
                    {vertical.name}
                  </span>
                )}
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-2">
                  {problem.title}
                </h1>
                <p className="text-slate-600 text-sm md:text-base line-clamp-2">
                  {problem.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Sticky Problem Header - Appears when scrolling */}
        <div 
          className={`fixed top-16 md:top-20 left-0 right-0 z-40 bg-white border-b border-slate-200 shadow-sm transition-all duration-300 ${
            isHeaderSticky ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
          }`}
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-500 flex-shrink-0">
                {getIcon(problem.icon || "AlertCircle", "h-4 w-4")}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-slate-900 truncate text-sm md:text-base">
                  {problem.title}
                </h2>
                <p className="text-xs text-amber-700 truncate">
                  <span className="font-medium">{t('problem.businessChallenge')}:</span> {problem.challenge}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setShowLeadForm(true)}
                className="bg-[#0066CC] hover:bg-[#0052A3] rounded-full text-xs md:text-sm whitespace-nowrap"
              >
                {t('problem.getConsultation')}
              </Button>
            </div>
          </div>
        </div>

        {/* Business Challenge Badge */}
        <section className="py-4 bg-amber-50/50 border-b border-amber-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2">
              <span className="text-amber-700 font-medium text-sm">{t('problem.businessChallenge')}:</span>
              <span className="text-amber-800 text-sm">{problem.challenge}</span>
            </div>
          </div>
        </section>

        {/* Main Content Area with Sticky Sidebar */}
        <section className="py-8 md:py-12" ref={contentRef}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative">
              {/* Main Content - 2 cols */}
              <div className="lg:col-span-2">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">
                  {t('problem.solutionApproach')}
                </h2>
                
                {solution ? (
                  <SolutionTimeline solution={solution} />
                ) : (
                  <div className="text-center py-12 bg-slate-50 rounded-2xl">
                    <p className="text-slate-500">{t('problem.solutionDetails')}</p>
                  </div>
                )}

                {/* Value Delivered - Inline */}
                {solution?.value_delivered && Object.keys(solution.value_delivered).length > 0 && (
                  <div className="mt-12 pt-8 border-t border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900 mb-4">
                      {t('problem.valueDelivered')}
                    </h3>
                    <p className="text-slate-600 mb-6 text-sm">
                      {t('problem.valueDescription')}
                    </p>
                    <ValueDelivered values={solution.value_delivered} />
                  </div>
                )}
              </div>
              
              {/* Sidebar - Sticky At A Glance */}
              <div className="lg:col-span-1" ref={sidebarRef}>
                <div className="lg:sticky lg:top-36 space-y-4 transition-all">
                  {/* At A Glance Box */}
                  <AtAGlanceBox vertical={vertical} solution={solution} />
                  
                  {/* Quick Contact Card */}
                  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0066CC] to-[#0052A3] p-5 text-white">
                    <h3 className="text-base font-semibold mb-2">{t('sidebar.needExpertGuidance')}</h3>
                    <p className="text-blue-100 text-sm mb-4">
                      {t('sidebar.consultantHelp')}
                    </p>
                    <OdooBooking
                      verticalId={vertical?.id || ""}
                      verticalName={vertical?.name || ""}
                      buttonText={t('common.scheduleFreeConsultation')}
                      buttonClassName="w-full bg-white text-[#0066CC] hover:bg-blue-50 font-semibold gap-2"
                    />
                  </div>

                  {/* Link to Vertical Details */}
                  {hasVerticalContent && (
                    <Link to={`/vertical/${vertical.id}`} className="block">
                      <div className="rounded-xl border border-slate-200 p-4 hover:border-[#0066CC] hover:bg-[#E6F0FA]/30 transition-all group">
                        <div className="flex items-center gap-3">
                          <Info className="h-5 w-5 text-[#0066CC]" />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900 text-sm group-hover:text-[#0066CC]">
                              {t('problem.viewIndustryDetails')}
                            </p>
                            <p className="text-xs text-slate-500">
                              {t('problem.viewPricingTestimonials')}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#0066CC] group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Clients Who Benefited - With Hover Profiles */}
        {clients.length > 0 && (
          <section className="py-12 md:py-16 bg-slate-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                  {t('problem.clientsBenefited')}
                </h2>
                <p className="text-slate-600 text-sm">{t('problem.clientsHoverHint')}</p>
              </div>
              <ClientLogosWithProfiles clients={clients} />
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-[#0066CC]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
              {t('problem.facingSimilarChallenges')}
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto text-sm md:text-base">
              {t('problem.facingDescription')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                onClick={() => setShowLeadForm(true)}
                className="bg-white text-[#0066CC] hover:bg-blue-50 rounded-full px-8"
                data-testid="get-started-btn"
              >
                {t('common.getStarted')}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Link to="/verticals">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-8 border-white/30 text-white hover:bg-white/10"
                >
                  {t('problem.exploreMoreSolutions')}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Sticky Contact Bar */}
      <StickyContactBar
        onOpenLeadForm={() => setShowLeadForm(true)}
        verticalId={vertical?.id}
        verticalName={vertical?.name}
      />
      
      {/* Lead Capture Form Modal */}
      <LeadCaptureForm
        isOpen={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        problemTitle={problem?.title || ""}
        verticalName={vertical?.name || ""}
      />

      <Footer />
    </div>
  );
}
